/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-alert */
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Img } from 'react-image'
import { NavLink } from 'react-router-dom'
import ReactTimeAgo from 'react-time-ago'
import { toast } from 'react-toastify'
import { ReactComponent as CheckIcon } from '@material-design-icons/svg/round/check.svg'
import { useMutation, useQueryClient } from '@tanstack/react-query'
// import { unlike } from 'api'
import {
  deleteCancelReportPost,
  deletePost,
  likeRequest,
  postReportPost,
  unlikeRequest,
} from 'api'

import IconButton from 'components/common/IconButton'
import Loader from 'components/common/Loader'
import CopyIcon from 'components/icons/CopyIcon'
import FullScreenIcon from 'components/icons/FullScreenIcon'
import LikeIcon from 'components/icons/LikeIcon'
import UnLikeIcon from 'components/icons/UnLikeIcon'
import ViewsIcon from 'components/icons/ViewsIcon'
import LightBox from 'components/LightBox'
import SettingsPost from 'components/SettingsPost'
import VerifiedIcon from 'components/VerifiedIcon'
import { setLogInModalOpen } from 'store/reducers/modalReducer'
import useFetchRequestMyProfile from 'hooks/query/useFetchRequestMyProfile'
import { useAppSelector } from 'hooks/store'
import useCopy from 'hooks/useCopy'
import useModal from 'hooks/useModal'
import { PostFeed } from 'types/Post'

import 'react-image-lightbox/style.css'

import ConfirmModal from 'components/modals/ConfirmModal'
import { SettingConfirmModalProps } from 'components/SettingsPost/SettingsPost'
import CommentIcon from '../icons/CommentIcon'

import c from './TrendingPostItem.module.scss'

interface PostProps {
  post: PostFeed
  imgClassName?: string
  withLink?: boolean
  trending?: boolean
  feed?: boolean
}

function TrendingPostItem({
  post,
  imgClassName = '',
  withLink = false,
  trending = false,
  feed = false,
}: PostProps) {
  const { t } = useTranslation()
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const language = useAppSelector((state) => state.settings.language)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [isLiked, setIsLiked] = useState<boolean>(post.is_liked)
  const [likesCount, setLikesCount] = useState<number>(post.likes_count)
  const [commentsCount, setCommentsCount] = useState<number>(
    post.comments_count,
  )
  const [videoError, setVideoError] = useState(false)
  const [videoLoading, setVideoLoading] = useState(true)
  const { isCopied, handleCopy } = useCopy(post.assets?.[0]?.source)
  const [isConfirmModal, setIsConfirmModal] =
    useState<SettingConfirmModalProps>({
      isOpen: false,
      isDelete: false,
      isReport: false,
    })

  const queryClient = useQueryClient()

  const handleImageLoaded = useCallback(() => {
    setIsImageLoaded(true)
  }, [])

  const { data: user } = useFetchRequestMyProfile()
  const mutationLike = useMutation<void, Error>(() => {
    return likeRequest(post.id!)
  })
  const mutationUnLike = useMutation<void, Error>(() => {
    return unlikeRequest(post.id!)
  })

  const mutationDelete = useMutation<void, Error>(() => {
    return deletePost(post.id!)
  })
  const mutationReport = useMutation<void, Error>(() => postReportPost(post.id))
  const mutationCancelReport = useMutation<void, Error>(() =>
    deleteCancelReportPost(post.id),
  )

  const { logInModalOpen } = useAppSelector((state) => state.modal)
  const { open: openLogInModal } = useModal(logInModalOpen, setLogInModalOpen)

  const copyIcon = useMemo(() => {
    return isCopied ? (
      <CheckIcon className={c.icon} style={{ fill: 'currentColor' }} />
    ) : (
      <CopyIcon />
    )
  }, [isCopied])

  const handleFullScreenOpen = () => {
    setIsLightboxOpen(true)
  }

  const handleFullScreenClose = () => {
    setIsLightboxOpen(false)
  }

  const likeHandler = () => {
    if (user?.id !== post.user.id) {
      if (isLiked) {
        setLikesCount((prev) => prev - 1)
        setIsLiked(false)
        mutationUnLike.mutateAsync().then(() => {
          queryClient.invalidateQueries(['post', post.id])
          queryClient.invalidateQueries(['myProfile', post.id])
          queryClient.refetchQueries([`infinite-profilePosts-${post.user.id}`])
        })
      } else {
        setLikesCount((prev) => prev + 1)
        setIsLiked(true)
        mutationLike.mutateAsync().then(() => {
          queryClient.invalidateQueries(['post', post.id])
          queryClient.invalidateQueries(['myProfile', post.id])
          queryClient.refetchQueries([`infinite-profilePosts-${post.user.id}`])
        })
      }
    } else {
      toast.error("You can't like your own post")
    }
  }

  const deleteYourPost = useCallback(() => {
    try {
      mutationDelete.mutate()
      toast.success(t('general.post.successDelete'))
    } catch (error) {
      console.log(error)
      toast.success(t('error.somethingWrong'))
    }
  }, [mutationDelete])

  const reportPost = useCallback(() => {
    try {
      if (post.is_strike) {
        mutationCancelReport.mutateAsync().then(() => {
          queryClient.invalidateQueries({
            queryKey: [`post`],
          })
          queryClient.refetchQueries({
            queryKey: [`post`],
          })
          toast.success(t('general.post.successCancelReport'))
        })
      } else {
        mutationReport.mutateAsync().then(() => {
          queryClient.invalidateQueries({
            queryKey: [`post`],
          })
          queryClient.refetchQueries({
            queryKey: [`post`],
          })
          toast.success(t('general.post.successReport'))
        })
      }
    } catch (error) {
      console.log(error)
      toast.success(t('error.somethingWrong'))
    }
  }, [mutationReport])

  const cancelConfirm = useCallback(() => {
    setIsConfirmModal({
      isDelete: false,
      isOpen: false,
      isReport: false,
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (isConfirmModal.isDelete) {
      deleteYourPost()
    }
    if (isConfirmModal.isReport) {
      reportPost()
    }
    cancelConfirm()
  }, [isConfirmModal, deleteYourPost])

  const confirmModalHeading = useMemo(() => {
    if (isConfirmModal.isDelete) {
      return t('general.post.deleteTextModal')
    }
    if (isConfirmModal.isReport) {
      return t('general.post.reportTextModal')
    }

    return ''
  }, [isConfirmModal])

  return (
    <div className={c.post}>
      <ConfirmModal
        isModalOpen={isConfirmModal.isOpen}
        confirmFunc={handleConfirm}
        cancelFunc={cancelConfirm}
        heading={confirmModalHeading}
      />
      <div className={c.postTop}>
        <div className={c.postUser}>
          <div className={c.avatarWrapper}>
            {post.user.avatar && (
              <img className={c.avatar} src={post.user.avatar} alt="" />
            )}
          </div>
          <div className={c.nicknameWrapper}>
            <p className={c.nickname}>
              {post.user.nickname}{' '}
              {post.user.is_paid_subscription && <VerifiedIcon />}
            </p>
            {post.created_at && (
              <p className={c.date}>
                <ReactTimeAgo
                  date={new Date(post.created_at)}
                  locale={language === 'en' ? 'en-US' : 'ru'}
                />
              </p>
            )}
          </div>
          <NavLink
            className={c.link}
            to={`/profile/${post.user.nickname}`}
            end
          />
        </div>
        <div>
          <SettingsPost
            userData={user}
            postData={post}
            setIsConfirmModal={setIsConfirmModal}
          />
        </div>
      </div>
      <div
        className={`${
          trending || feed || post.assets?.[0].source_type === 'video'
            ? c.postImgWrapperTrending
            : c.postImgWrapper
        } ${imgClassName}`}
      >
        {post.assets?.[0].source_type !== 'video' && post.assets?.[0].url && (
          <div className={c.imgWrapper}>
            <div className={c.postImgContainer}>
              <Img
                src={post.assets?.[0].url}
                className={c.postImg}
                loader={<Loader />}
                onLoad={handleImageLoaded}
              />
              <button
                type="button"
                onClick={handleFullScreenOpen}
                className={c.wrapper}
              >
                {isImageLoaded && (
                  <div className={c.fullScreenContainer}>
                    <div className={c.iconSetting}>
                      <FullScreenIcon />
                    </div>
                  </div>
                )}
              </button>
            </div>
            <NavLink to={`/trending-post/${post.id}`} end className={c.link} />
          </div>
        )}
        {post.assets?.[0]?.source_type === 'video' && (
          <div className={c.imgWrapper}>
            <div className={c.postImgContainer}>
              {videoLoading && !videoError && post.assets?.[0]?.url && (
                <div className={c.videoLoader}>
                  <Loader />
                  <span>Loading video...</span>
                </div>
              )}

              {post.assets?.[0]?.url && !videoError && (
                <video
                  src={post.assets[0].url}
                  className={c.postImg}
                  loop
                  controls
                  preload="metadata"
                  playsInline
                  onLoadedData={() => {
                    setVideoLoading(false)
                    console.log('Video loaded successfully:', post.id)
                  }}
                  onError={(e) => {
                    setVideoError(true)
                    setVideoLoading(false)
                    console.error('Video failed to load:', {
                      postId: post.id,
                      url: post.assets[0].url,
                      error: e.currentTarget.error?.message,
                    })
                  }}
                  style={{
                    display: videoLoading ? 'none' : 'block',
                    opacity: videoLoading ? 0 : 1,
                    transition: 'opacity 0.3s ease',
                  }}
                />
              )}

              {(videoError || !post.assets?.[0]?.url) && (
                <div className={c.videoErrorState}>
                  <div className={c.videoIcon}>⚠️</div>
                  <div className={c.errorText}>
                    <span className={c.errorTitle}>Video unavailable</span>
                    <span className={c.errorSubtitle}>
                      {!post.assets?.[0]?.url
                        ? 'Video link is missing'
                        : 'Failed to load video'}
                    </span>
                  </div>

                  <IconButton
                    className={c.retryButton}
                    onClick={() => {
                      setVideoError(false)
                      setVideoLoading(true)
                    }}
                    disabled={videoLoading}
                  >
                    {videoLoading ? 'Retrying...' : 'Try again'}
                  </IconButton>
                </div>
              )}
            </div>
          </div>
        )}

        {isLightboxOpen && post.assets?.[0].url && (
          <LightBox
            imageUrl={post.assets?.[0].url}
            onCloseRequest={handleFullScreenClose}
          />
        )}
      </div>
      <div className={c.postBottom}>
        {post.assets?.[0]?.source_type !== 'image' && (
          <>
            <div className={c.text}>{post.assets?.[0]?.source}</div>
            <IconButton
              className={c.postCopy}
              disabled={isCopied}
              onClick={handleCopy}
            >
              {copyIcon}
            </IconButton>
          </>
        )}
      </div>
      <div className={c.postTags}>
        {post.handler === 'William_Henry_Harrison' && (
          <div className={c.textStyleContainer}>
            <span className={c.textStyleSub}>{t('feed.dalle3')}</span>
          </div>
        )}
        {post.is_related && (
          <div className={c.textStyleContainer}>
            <span className={c.textStyleSub}>{t('feed.recommended')}</span>
          </div>
        )}
        {post.assets?.[0]?.style && post.assets?.[0]?.style !== 'undefined' && (
          <div className={c.textStyleContainer}>
            <span className={c.textStyle}>{post.assets?.[0]?.style}</span>
          </div>
        )}
      </div>
      <div className={c.postFeedBack}>
        <div className={c.toolsFeedback}>
          <div
            className={c.postToolButton}
            onClick={!user?.id ? openLogInModal : likeHandler}
            onKeyDown={!user?.id ? openLogInModal : likeHandler}
            role="button"
            tabIndex={0}
          >
            <div className={c.postToolButtonIcon}>
              <UnLikeIcon className={c.iconSetting} />
              <div className={`${c.favoredIcon} ${isLiked ? c.favored : ''}`}>
                <LikeIcon className={c.iconSetting} />
              </div>
            </div>
            <p className={c.postToolButtonCount}>{likesCount}</p>
          </div>
          <NavLink
            to={`/trending-post/${post.id}/comments`}
            className={c.postToolButton}
          >
            <div className={c.postToolButtonIcon}>
              <CommentIcon className={c.iconSetting} />
            </div>
            <p className={c.postToolButtonCount}>{post.comments_count}</p>
          </NavLink>
        </div>
        <div className={c.postViews}>
          {`${post.views_count} `} <ViewsIcon />
        </div>
      </div>
    </div>
  )
}

export default TrendingPostItem
